import { loadStripe } from '@stripe/stripe-js'

import Settings from 'helpers/settings'
import { postV2, getV2, patchV2, putV2 } from 'api'

const capabilities = { card: 'card', ach: 'us_bank_account' }

const StripeHelpers = {
  capabilities,

  loadStripe() {
    return loadStripe(Settings.STRIPE_PUBLISHABLE_KEY)
  },

  async setDefaultPaymentMethod(paymentMethodId) {
    const body = { body: { invoice_settings: { default_payment_method: paymentMethodId } } }
    const { stripe_customer } = await patchV2('/v2/client/customers', body)
    return stripe_customer
  },

  async attachPaymentMethodThirdParty(jobSecretAliasId, paymentMethodId) {
    const body = { payment_method_id: paymentMethodId }
    const { message } = await postV2(`/v2/third_party/jobs/${jobSecretAliasId}/payment_methods/attach`, { body })
    return message
  },

  async capturePaymentIntentThirdParty(jobSecretAliasId, paymentMethodId) {
    const body = { payment_method_id: paymentMethodId }
    const { payment_intent } = await postV2(`/v2/third_party/jobs/${jobSecretAliasId}/payment_intent/capture`, { body })
    return payment_intent
  },

  async capturePaymentIntent(swipecastJobId, payment_method_id) {
    const body = { payment_method_id }
    const { payment_intent } = await postV2(`/v2/client/jobs/${swipecastJobId}/payment_intent/capture`, { body })
    return payment_intent
  },

  async createStripeCustomer() {
    const { stripe_customer } = await postV2('/v2/client/customers')
    return stripe_customer
  },

  async createStripeCustomerThirdParty(jobSecretAliasId) {
    const { stripe_customer } = await postV2(`/v2/third_party/jobs/${jobSecretAliasId}/customer`)
    return stripe_customer
  },

  async createSetupIntent(pmTypes = [StripeHelpers.capabilities.card]) {
    const body = { payment_method_types: pmTypes }
    const { setup_intent } = await postV2('/v2/client/setup_intents', { body })
    return setup_intent
  },

  async createSetupIntentThirdParty(jobSecretAliasId, pmTypes = [StripeHelpers.capabilities.card]) {
    const body = { payment_method_types: pmTypes }
    const { setup_intent } = await postV2(`/v2/third_party/jobs/${jobSecretAliasId}/setup_intent`, { body })
    return setup_intent
  },

  async ensurePaymentIntent(swipecastJobId, action) {
    const body = { current_action: action }
    const { payment_intent } = await putV2(`/v2/client/jobs/${swipecastJobId}/payment_intent`, { body })
    return payment_intent
  },

  async createPaymentIntentThirdParty(jobSecretAliasId) {
    const { payment_intent } = await putV2(`/v2/third_party/jobs/${jobSecretAliasId}/payment_intent`)
    return payment_intent
  },

  async ensurePaymentIntentThirdParty(jobSecretAliasId) {
    let paymentIntent
    try {
      paymentIntent = await this.getPaymentIntentThirdParty(jobSecretAliasId)
    } catch (error) {
      if (error.status === 404) paymentIntent = await this.createPaymentIntentThirdParty(jobSecretAliasId)
    }
    return paymentIntent
  },

  async ensureStripeCustomer() {
    let customer
    try {
      customer = await this.getStripeCustomer()
    } catch (error) {
      console.log(error.status, error) // TODO remove
      if (error.status === 404) customer = await this.createStripeCustomer()
    }
    return customer
  },

  async ensureStripeCustomerThirdParty(jobSecretAliasId) {
    let customer
    try {
      customer = await this.getStripeCustomerThirdParty(jobSecretAliasId)
    } catch (error) {
      if (error.status === 404) customer = await this.createStripeCustomerThirdParty(jobSecretAliasId)
    }
    return customer
  },

  async getStripePaymentMethods() {
    const { payment_methods } = await getV2('/v2/client/payment_methods')
    return payment_methods
  },

  async getStripePaymentMethod(stripePaymentMethodId) {
    const { payment_method } = await getV2(`/v2/client/payment_methods/${stripePaymentMethodId}`)
    return payment_method
  },

  async getStripeAccountPaymentCapabilitiesThirdParty(swipecastUserId) {
    const { data } = await getV2(`/v2/client/users/${swipecastUserId}/payment_capabilities`)
    return data
  },

  async getStripeCustomer() {
    const { stripe_customer } = await getV2('/v2/client/customers')
    return stripe_customer
  },

  async getPaymentIntentThirdParty(jobSecretAliasId) {
    const { payment_intent } = await getV2(`/v2/third_party/jobs/${jobSecretAliasId}/payment_intent`)
    return payment_intent
  },

  async getStripeCustomerThirdParty(jobSecretAliasId) {
    const { stripe_customer } = await getV2(`/v2/third_party/jobs/${jobSecretAliasId}/customer`)
    return stripe_customer
  },

  /**
   * @description - it accepts card from both sources and payment methods
   */
  getCardLabel(card) {
    return `${StripeHelpers.getCardBrandLabel(card)} •••• ${card.last4} · exp ${card.exp_month}/${card.exp_year}`
  },

  /**
   * @description - it accepts us_bank_account from ACH payment methods
   */
  getUSBankAccountLabel(usBankAccount) {
    return `${usBankAccount.bank_name} · *${usBankAccount.last4}`
  },

  /**
   * @description - Returns a formatted card brand label. Abbreviates "American Express" to "Amex".
   * @param {object} card - The card object containing brand information.
   * @returns {string} - The formatted card brand label.
   */
  getCardBrandLabel(card) {
    const brandMap = {
      amex: 'Amex',
      visa: 'Visa',
      mastercard: 'Mastercard',
      discover: 'Discover',
      jcb: 'JCB',
      diners: 'Diners Club',
      unionpay: 'UnionPay',
      // Add more mappings as needed
    }
    const brand = card.brand?.toLowerCase()
    return brandMap[brand] || (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : '')
  },

  /**
   * @description - keep in mind that source and payment methods have different structures
   * @param stripeCustomer {object} - a stripe customer object as returned by our API
   * @param useFallback {boolean} - whether to return a dummy card if no card is found
   * @returns {{last4: (string|*), expMonth: *, expYear: *, brand: (string|*), id, object: (string|*)}|{last4: string, expMonth: string, expYear: string, brand: string, id: string, object: string}|null}
   */
  getCustomerDefaultPaymentMethod(stripeCustomer, useFallback = false) {
    if (!stripeCustomer || !stripeCustomer.data) return null

    const defaultPaymentMethod = stripeCustomer.data.invoice_settings?.default_payment_method

    if (defaultPaymentMethod?.type === 'card') {
      const { card } = defaultPaymentMethod
      return {
        isPaymentMethod: true,
        label: StripeHelpers.getCardLabel(card),
        last4: card.last4,
        expMonth: card.exp_month,
        expYear: card.exp_year,
        brand: card.brand,
        id: defaultPaymentMethod.id,
        object: defaultPaymentMethod.object,
      }
    } else if (defaultPaymentMethod?.type === 'us_bank_account') {
      const { us_bank_account } = defaultPaymentMethod
      return {
        isPaymentMethod: true,
        label: StripeHelpers.getUSBankAccountLabel(us_bank_account),
        last4: us_bank_account.last4,
        bankName: us_bank_account.bank_name,
        id: defaultPaymentMethod.id,
        object: defaultPaymentMethod.object,
      }
    }

    const defaultSource = stripeCustomer.data.sources.data[0]

    if (defaultSource?.object === 'card') {
      return {
        isPaymentMethod: false,
        label: StripeHelpers.getCardLabel(defaultSource),
        last4: defaultSource.last4,
        expMonth: defaultSource.exp_month,
        expYear: defaultSource.exp_year,
        brand: defaultSource.brand,
        id: defaultSource.id,
        object: defaultSource.object,
      }
    }

    if (useFallback) return { last4: 'XXXX', expMonth: 'X', expYear: 'XXXX', brand: 'Unknown', id: 'N/A', object: 'card' }

    return null
  },
}

export default StripeHelpers
